export interface Bbox {
  lamin: number;
  lomin: number;
  lamax: number;
  lomax: number;
}

export interface TrailPoint {
  lat: number;
  lon: number;
  altitudeFt: number | null;
  ts: number;
  /** Heading at this fix (degrees true) — lets trails be drawn as smooth curves (see map/smooth.ts). */
  track?: number | null;
}

export interface Aircraft {
  icao24: string;
  callsign: string | null;
  lat: number;
  lon: number;
  altitudeFt: number | null;
  groundSpeedKt: number | null;
  track: number | null;
  verticalRateFpm: number | null;
  onGround: boolean;
  squawk: string | null;
  category: string | null;
  typeCode: string | null;
  typeDesc: string | null;
  registration?: string | null;
  lastSeen: number;
  sources: string[];
  /** Server history id of the flight in progress. */
  flightId?: number | null;
  trail: TrailPoint[];
}

/** One recorded flight from the server's history (see /api/aircraft/:icao24/flights). */
export interface FlightMeta {
  id: number;
  icao24: string;
  callsign: string | null;
  registration: string | null;
  typeCode: string | null;
  startTs: number;
  endTs: number;
  origin: string | null;
  dest: string | null;
  maxAltFt: number | null;
  points: number;
  closed: boolean;
  bbox: Bbox | null;
}

export interface HistoryPoint extends TrailPoint {
  groundSpeedKt: number | null;
  track: number | null;
  onGround: boolean;
}

/** A flight's whole recorded path, drawn on the map when a plane or past flight is selected. */
export interface FocusTrack {
  flight: FlightMeta;
  points: HistoryPoint[];
  /** True for the selected aircraft's flight in progress (keeps growing from live updates). */
  live: boolean;
}

export interface SourceHealth {
  name: string;
  ok: boolean;
  lastSuccess: number | null;
  lastError: string | null;
  lastErrorAt: number | null;
  lastAircraftCount: number;
  consecutiveFailures: number;
}

export interface Airport {
  ident: string;
  name: string;
  type: 'large_airport' | 'medium_airport' | 'small_airport';
  lat: number;
  lon: number;
  iata: string | null;
  municipality: string | null;
}

export interface RouteInfo {
  callsign: string;
  airlineName: string | null;
  originIcao: string | null;
  originName: string | null;
  originMunicipality: string | null;
  originLat: number | null;
  originLon: number | null;
  destinationIcao: string | null;
  destinationName: string | null;
  destinationMunicipality: string | null;
  destinationLat: number | null;
  destinationLon: number | null;
}

export interface AircraftInfo {
  icao24: string;
  registration: string | null;
  typeCode: string | null;
  typeName: string | null;
  manufacturer: string | null;
  operator: string | null;
}

export interface PhotoInfo {
  thumbnail: string;
  large: string;
  link: string;
  photographer: string | null;
}

export interface Enrichment {
  icao24: string;
  callsign: string | null;
  route: RouteInfo | null;
  /**
   * Where `route` comes from, after the server vets the flight number's usual
   * route against the live aircraft: as listed, this flight number's own
   * recent history, or just what was observed (departure / airport on final).
   */
  routeSource?: 'flight-number' | 'history' | 'observed' | null;
  /** Why the flight number's usual route was set aside, when it was. */
  routeNote?: string | null;
  aircraft: AircraftInfo | null;
  photo: PhotoInfo | null;
}

export type BoardStatus = 'ground' | 'departing' | 'enroute' | 'approaching';

/** One row of an airport arrivals/departures board — see server/src/airportBoard.ts. */
export interface BoardFlight {
  icao24: string;
  callsign: string | null;
  typeCode: string | null;
  /** The other end of the leg: where an arrival came from / a departure is headed. */
  otherIcao: string | null;
  lat: number;
  lon: number;
  altitudeFt: number | null;
  groundSpeedKt: number | null;
  onGround: boolean;
  distanceKm: number;
  /** Arrivals only; null when speed is unknown/too low to extrapolate. */
  etaMs: number | null;
  status: BoardStatus;
}

export interface AirportBoard {
  airport: Airport;
  arrivals: BoardFlight[];
  departures: BoardFlight[];
  onGroundCount: number;
}

export interface RouteEndpoints {
  originIcao: string | null;
  originLat: number;
  originLon: number;
  destinationIcao: string | null;
  destinationLat: number;
  destinationLon: number;
}

/**
 * One instrument approach procedure rendered by the "hobbyist" aviation
 * view as a 2D overlay: a centreline line from the computed Final Approach
 * Fix to the runway threshold, and a short dashed projection of the glide
 * slope past the threshold (its ground intersect) so the descent plane is
 * legible at a glance. Coordinates are public-domain thresholds from the
 * OurAirports runways.csv; `courseDeg` is the runway heading in degrees
 * true and matches the localizer course for the seeded ILS approaches.
 */
export interface ApproachProcedure {
  airportIcao: string;
  airportName?: string;
  runwayIdent: string;
  type: 'ILS' | 'RNAV' | 'VOR' | 'LOC';
  thresholdLat: number;
  thresholdLon: number;
  /** Localizer course / runway heading in degrees true. */
  courseDeg: number;
  /** Glide-slope angle in degrees (3.0 standard for ILS). Optional for non-precision approaches. */
  glideSlopeDeg?: number;
  /** FAF-to-threshold distance in nautical miles back along the back-azimuth. Default 7. */
  approachNm?: number;
  /** Length of the glide-slope ground projection past the threshold, in NM. Default 1.5. */
  glideSlopeLeadNm?: number;
  /** Localizer frequency string for the button-hover label, e.g. "110.30". */
  localizerFreq?: string;
  /** Final approach fix ident (e.g. "WAVUN") — shown as a waypoint marker. */
  fafIdent?: string;
  /** True for large-airport approaches — drawn more prominently on the map. */
  prominent?: boolean;
}

export type TypeFilter = 'all' | 'airliner' | 'ga' | 'heli' | 'military';

export interface Filters {
  minAltFt: number | null;
  maxAltFt: number | null;
  showGround: boolean;
  /** Also show aircraft parked on the ground whose transponder has gone quiet (kept up to 12 h at their last spot). */
  showParked: boolean;
  emergencyOnly: boolean;
  types: TypeFilter;
  /** Isolate to one specific ICAO aircraft type designator (e.g. "B738"); null = all models. */
  model: string | null;
}

export const DEFAULT_FILTERS: Filters = {
  minAltFt: null,
  maxAltFt: null,
  showGround: true,
  showParked: true,
  emergencyOnly: false,
  types: 'all',
  model: null,
};

export const EMERGENCY_SQUAWKS = new Set(['7500', '7600', '7700']);

/** On the ground and silent for this long = parked (drawn dimmed, at its last known spot). */
export const PARKED_AFTER_MS = 2 * 60_000;

export function isParked(ac: Aircraft, now = Date.now()): boolean {
  return ac.onGround && now - ac.lastSeen > PARKED_AFTER_MS;
}

/** A watched aircraft, matched either by exact ICAO24 hex or by callsign prefix (case-insensitive — "UAL" watches all United flights). */
export interface WatchEntry {
  id: string;
  kind: 'icao24' | 'callsign';
  value: string;
}

/** The watchlist entry (if any) that matches this aircraft — exact ICAO24 or callsign-prefix match. */
export function findWatchEntry(ac: Aircraft, watchlist: WatchEntry[]): WatchEntry | undefined {
  if (watchlist.length === 0) return undefined;
  const icao = ac.icao24.toLowerCase();
  const callsign = ac.callsign?.trim().toUpperCase();
  return watchlist.find((w) =>
    w.kind === 'icao24'
      ? icao === w.value.toLowerCase()
      : callsign != null && callsign.startsWith(w.value.toUpperCase()),
  );
}

export function isWatched(ac: Aircraft, watchlist: WatchEntry[]): boolean {
  return findWatchEntry(ac, watchlist) != null;
}

/** User-drawn coverage circle (Layers menu "Coverage circle" tool): pins the live-data subscription to a fixed circular area instead of following pan/zoom. */
export interface CoverageCircle {
  lat: number;
  lon: number;
  radiusKm: number;
}

export function bboxToString(b: Bbox): string {
  const r = (n: number) => n.toFixed(4);
  return `${r(b.lamin)},${r(b.lomin)},${r(b.lamax)},${r(b.lomax)}`;
}

export function bboxFromString(s: string | null): Bbox | null {
  if (!s) return null;
  const parts = s.split(',').map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [lamin, lomin, lamax, lomax] = parts;
  if (lamin >= lamax || lomin >= lomax) return null;
  return { lamin, lomin, lamax, lomax };
}
